import React, { Fragment, useRef, useState, useEffect } from 'react';
import { css } from 'twin.macro';
import { Map, Marker, GoogleApiWrapper } from 'google-maps-react';
import { FormControl, Form } from 'react-bootstrap';
import { debounce, getAddressFromLatLng, getLatLngFromAddress } from '../../../../_helpers';
import { VscLoading } from 'react-icons/vsc';
import { MdMyLocation, MdLocationOn } from 'react-icons/md';
import redMarker from '../../../../../src/assets/markers/red.png';
import { MapStyledDiv, OpenInGoogleMapDiv, locationMarkerStyles } from '../Styles/EventLocationStyles';
import googleMapsIcon from '../../../../../src/assets/icons/google/google-maps.png';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';

const extractCoordinates = (coord) => {
    if (coord.latLng) {
        const latLng = coord.latLng;
        return {
            latitude: latLng.lat(),
            longitude: latLng.lng(),
        };
    }
    return null;
};

const openInGoogleMaps = ({ lat, lng } = {}) => {
    const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    window.open(url, '_blank');
};

function EventLocationMapComponent({
    google,
    setValue = () => {},
    isDisabled,
    elementRef,
    data,
    defaultLocation,
    label = '',
    helpText = 'Type in event location and drag the marker or click at the correct location.',
    onLocationChange = () => {},
    view,
    ...rest
}) {
    // Default to Philadelphia coordinates if no location provided
    const defaultLat = defaultLocation?.lat || data?.defaultLatLong?.[0] || 39.9526;
    const defaultLng = defaultLocation?.lng || data?.defaultLatLong?.[1] || -75.1652;

    const [lat, setLat] = useState(defaultLat);
    const [lng, setLng] = useState(defaultLng);
    const [locationName, setLocationName] = useState(defaultLocation?.address || '');
    const [loadingLatLng, setLoadingLatLng] = useState(false);
    const [mapCenter, setMapCenter] = useState({ lat: defaultLat, lng: defaultLng });
    const [useCurrentLocation, setUseCurrentLocation] = useState(false);

    const addressRef = useRef();

    // Update map center when lat/lng changes
    useEffect(() => {
        setMapCenter({ lat, lng });
    }, [lat, lng]);

    useEffect(() => {
        if (defaultLocation?.lat && defaultLocation?.lng) {
            setLat(defaultLocation.lat);
            setLng(defaultLocation.lng);
            if (defaultLocation.address && addressRef.current) {
                addressRef.current.value = defaultLocation.address;
                setLocationName(defaultLocation.address);
            }
        } else if (defaultLocation?.address && view) {
            setExternalCalendarLocation(defaultLocation.address);
        }
    }, [defaultLocation]);

    const setExternalCalendarLocation = async (address) => {
        try {
            const { latitude, longitude } = await getLatLngFromAddress(defaultLocation?.address);
            setMapCenter({ lat: latitude, lng: longitude });
            setLat(latitude);
            setLng(longitude);
            setLocationName(address);
        } catch (error) {
            console.error('setExternalCalendarLocation::', error);
        }
    };

    const updateLocation = async ({ latitude, longitude, address = null }) => {
        try {
            setLat(latitude);
            setLng(longitude);

            if (data?.name && Array.isArray(data.name)) {
                setValue(data.name[0], latitude);
                setValue(data.name[1], longitude);
            }

            const resolvedAddress = address || (await getAddressFromLatLng(latitude, longitude));

            if (addressRef.current) {
                addressRef.current.value = resolvedAddress;
            }

            setLocationName(resolvedAddress);

            onLocationChange({
                lat: latitude,
                lng: longitude,
                address: resolvedAddress,
            });

            return resolvedAddress;
        } catch (err) {
            console.error('updateLocation:', err);
            throw err;
        }
    };

    const moveMarker = async (t, map, coord) => {
        try {
            const coordinates = extractCoordinates(coord);
            if (coordinates) {
                updateLocation(coordinates);
            }
        } catch (err) {
            console.error('moveMarker:', err);
        }
    };

    const handleGetLatLngFromAddress = async (e) => {
        try {
            const searchText = e.target.value;
            if (!searchText) return;
            setLoadingLatLng(true);
            let addressData;
            if (data?.name && Array.isArray(data.name)) {
                addressData = await getLatLngFromAddress(searchText, data.name[0], data.name[1], null, setValue);
            } else {
                addressData = await getLatLngFromAddress(searchText);
            }

            if (addressData) {
                const { latitude, longitude } = addressData;
                await updateLocation({ latitude, longitude, address: searchText });
            } else {
                setLocationName(searchText);
            }
        } catch (err) {
            console.error('handleGetLatLngFromAddress::', err);
        } finally {
            setLoadingLatLng(false);
        }
    };

    const onMapClicked = async (mapProps, map, clickEvent) => {
        try {
            const latitude = clickEvent.latLng.lat();
            const longitude = clickEvent.latLng.lng();

            setLat(latitude);
            setLng(longitude);

            if (data?.name) {
                setValue(data.name[0], latitude);
                setValue(data.name[1], longitude);
            }

            const addressName = await getAddressFromLatLng(latitude, longitude);
            if (addressRef.current) {
                addressRef.current.value = addressName;
            }
            setLocationName(addressName);

            onLocationChange({
                lat: latitude,
                lng: longitude,
                address: addressName,
            });
        } catch (err) {
            console.error('onMapClicked::', err);
        }
    };

    const getCurrentLocation = () => {
        if (navigator.geolocation) {
            setUseCurrentLocation(true);
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    try {
                        const { latitude, longitude } = position.coords;
                        await updateLocation({ latitude, longitude });
                    } catch (error) {
                        console.error('getCurrentLocation:Processing:Error::', error);
                    } finally {
                        setUseCurrentLocation(false);
                    }
                },
                (error) => {
                    console.error('getCurrentLocation:Error::', error);
                    setUseCurrentLocation(false);
                }
            );
        }
    };

    return (
        <Fragment>
            {google && (
                <Fragment>
                    <div className="">
                        {!isDisabled && <small className="d-block">{helpText}</small>}
                        {!isDisabled && (
                            <div className="position-relative">
                                <FormControl
                                    ref={addressRef}
                                    className="w-100"
                                    onChange={debounce(handleGetLatLngFromAddress, 1000)}
                                    placeholder="Enter event location"
                                    defaultValue={locationName}
                                />
                                {loadingLatLng ? (
                                    <VscLoading
                                        css={css`
                                            top: 10px;
                                            right: 10px;
                                        `}
                                        className="spin text-primary position-absolute"
                                    />
                                ) : (
                                    <MdLocationOn
                                        css={css`
                                            top: 10px;
                                            right: 10px;
                                            color: #50aeb0;
                                        `}
                                        className="position-absolute"
                                        size={20}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                    <MapStyledDiv>
                        <Map
                            google={google}
                            initialCenter={{ lat, lng }}
                            center={mapCenter}
                            zoom={15}
                            style={{
                                position: 'absolute',
                                width: '100%',
                                height: 'auto',
                            }}
                            streetViewControlOptions={{
                                position: google.maps.ControlPosition.RIGHT_TOP,
                            }}
                            zoomControlOptions={{
                                position: google.maps.ControlPosition.RIGHT_TOP,
                            }}
                            onClick={onMapClicked}
                        >
                            <Marker
                                {...rest}
                                draggable={!isDisabled}
                                onDragend={(t, map, coord) => moveMarker(t, map, coord)}
                                name={'event location'}
                                position={{ lat, lng }}
                                icon={{
                                    url: redMarker,
                                    scaledSize: new google.maps.Size(40, 40),
                                }}
                            />
                        </Map>

                        {!isDisabled && (
                            <div
                                css={locationMarkerStyles}
                                onClick={getCurrentLocation}
                                title="Use my current location"
                            >
                                {useCurrentLocation ? (
                                    <VscLoading className="spin" size={24} />
                                ) : (
                                    <MdMyLocation size={24} color="#50aeb0" />
                                )}
                            </div>
                        )}
                        {isDisabled && (
                            <OpenInGoogleMapDiv
                                onClick={() => {
                                    openInGoogleMaps({ lat, lng });
                                }}
                            >
                                <img src={googleMapsIcon} alt="google-maps" height="24" width="24" className="mr-2" />
                                Open in Google Maps
                            </OpenInGoogleMapDiv>
                        )}
                    </MapStyledDiv>
                </Fragment>
            )}
            {data?.name &&
                data.name.map((lalo, index) => (
                    <input
                        key={lalo}
                        hidden
                        name={lalo}
                        defaultValue={(data.defaultLatLong && data.defaultLatLong[index]) || 0}
                        ref={
                            elementRef &&
                            elementRef({
                                required: data.hasOwnProperty('required') ? data.required : true,
                                ...(data.validation || {}),
                            })
                        }
                    />
                ))}

            <Form.Text className="text-muted">
                {!isDisabled && (
                    <small>
                        Coordinates: {lat.toFixed(6)}, {lng.toFixed(6)}
                    </small>
                )}
            </Form.Text>
        </Fragment>
    );
}

const EventLocationMap = (props) => (
    <ComponentErrorBoundary componentErrorMessage={props?.componentErrorMessage || 'Failed to load Event Location Map'}>
        <EventLocationMapComponent {...props} />
    </ComponentErrorBoundary>
);
export default GoogleApiWrapper({
    apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
})(EventLocationMap);
